import { DatePipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core'
import { MatIcon } from '@angular/material/icon'
import { MatTooltip } from '@angular/material/tooltip'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { formatDayCount } from '../retention-day-count'

const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000

// A label centred on its own mark runs out of the box near either end of the scale, so past these
// points it is aligned to start or to end at the mark instead.
const ALIGN_TO_END_PERCENTAGE = 75
const ALIGN_TO_START_PERCENTAGE = 25

/** Which of the two dates a drag or a key press is moving. */
export type RetentionMarker = 'SOFT' | 'HARD'

/** What the timeline draws. Every date is in the reader's own time zone, as the dialog's are. */
export type RetentionTimelineView = {
  meetingDate: Date
  /** Where the two dates stood when the dialog opened, which the markers are dragged away from. */
  currentSoftDeletionDate: Date
  currentDeletionDate: Date
  softDeletionDate: Date
  deletionDate: Date
  /** Null when the organization sets no total, in which case the scale ends without a tick. */
  maximumTotalDate: Date | null
  /** Why a marker may stop before that tick, which the tick's own label cannot say. */
  maximumTotalTooltip: string
  /** Both caps stated outright, as the last thing the footer says. */
  limitsLabel: string
  /** The right-hand edge of the scale, which the dialog picks. */
  windowEndDate: Date
  /**
   * Whether the organization hides a meeting before deleting it at all. Where it does not, the two
   * dates are one and the same and only the deletion marker is drawn.
   */
  hasSoftDeletion: boolean
  /** Each marker's own position, in days later than the date it sits on now; negative once brought back in. */
  softExtensionInDays: number
  deletionExtensionInDays: number
  /** How far back each marker may be dragged, as a count of days that is zero or below. */
  minimumSoftExtensionInDays: number
  minimumDeletionExtensionInDays: number
  maximumSoftExtensionInDays: number
  maximumDeletionExtensionInDays: number
  /** The stretch between the two staged dates, which the meeting can still be recovered in. */
  recoveryWindowInDays: number
}

@Component({
  selector: 'app-retention-timeline',
  standalone: true,
  imports: [DatePipe, TranslatePipe, TranslateDirective, MatIcon, MatTooltip],
  templateUrl: './retention-timeline.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './retention-timeline.component.scss',
})
export class RetentionTimelineComponent {
  private translateService = inject(TranslateService)

  @Input({ required: true })
  view!: RetentionTimelineView

  /** How many days later than its current date each marker has been moved to. */
  @Output()
  softExtensionInDaysChange = new EventEmitter<number>()

  @Output()
  deletionExtensionInDaysChange = new EventEmitter<number>()

  @ViewChild('track', { static: true }) private track!: ElementRef<HTMLElement>

  /** The day alone: the times are in the fields below, where they can be read and edited. */
  readonly dateFormat = 'd MMM y'

  private draggedMarker: RetentionMarker | null = null

  get softExtendedByLabel(): string {
    return formatDayCount(this.translateService, Math.abs(this.view.softExtensionInDays))
  }

  get deletionExtendedByLabel(): string {
    return formatDayCount(this.translateService, Math.abs(this.view.deletionExtensionInDays))
  }

  /** Whether a marker has been brought back in, which the summary says in words rather than with a minus sign. */
  isReduced(marker: RetentionMarker): boolean {
    return this.extensionInDaysOf(marker) < 0
  }

  /** Where a date sits on the scale, clamped so a date past the edge still draws inside the box. */
  percentageFor(date: Date): number {
    const span = this.view.windowEndDate.getTime() - this.view.meetingDate.getTime()
    if (span <= 0) {
      return 0
    }
    const fraction = (date.getTime() - this.view.meetingDate.getTime()) / span
    return Math.min(100, Math.max(0, fraction * 100))
  }

  /** Whether a label at this date would run off the right of the box if it were centred on it. */
  isNearEnd(date: Date): boolean {
    return this.percentageFor(date) > ALIGN_TO_END_PERCENTAGE
  }

  /** The same at the left, where the meeting end of the scale is. */
  isNearStart(date: Date): boolean {
    return this.percentageFor(date) < ALIGN_TO_START_PERCENTAGE
  }

  /** The stretch between the two markers, which is the window the meeting can still be recovered in. */
  get recoverableWidthPercentage(): number {
    return Math.max(0, this.percentageFor(this.view.deletionDate) - this.percentageFor(this.view.softDeletionDate))
  }

  extensionInDaysOf(marker: RetentionMarker): number {
    return marker === 'SOFT' ? this.view.softExtensionInDays : this.view.deletionExtensionInDays
  }

  minimumExtensionInDaysOf(marker: RetentionMarker): number {
    return marker === 'SOFT' ? this.view.minimumSoftExtensionInDays : this.view.minimumDeletionExtensionInDays
  }

  maximumExtensionInDaysOf(marker: RetentionMarker): number {
    return marker === 'SOFT' ? this.view.maximumSoftExtensionInDays : this.view.maximumDeletionExtensionInDays
  }

  dateOf(marker: RetentionMarker): Date {
    return marker === 'SOFT' ? this.view.softDeletionDate : this.view.deletionDate
  }

  startDragging(event: PointerEvent, marker: RetentionMarker) {
    // Keeps the moves coming while the pointer runs past the edge of the marker, and stops the
    // browser reading the drag as a text selection.
    event.preventDefault()
    ;(event.target as HTMLElement).setPointerCapture(event.pointerId)
    this.draggedMarker = marker
  }

  continueDragging(event: PointerEvent) {
    const marker = this.draggedMarker
    if (marker !== null) {
      this.emitExtension(marker, this.extensionAt(event.clientX, marker))
    }
  }

  stopDragging() {
    this.draggedMarker = null
  }

  handleKeyDown(event: KeyboardEvent, marker: RetentionMarker) {
    const steps: Record<string, number> = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1, PageUp: 7, PageDown: -7 }
    if (Object.hasOwn(steps, event.key)) {
      event.preventDefault()
      this.emitExtension(marker, this.extensionInDaysOf(marker) + steps[event.key])
    } else if (event.key === 'Home') {
      event.preventDefault()
      this.emitExtension(marker, 0)
    } else if (event.key === 'End') {
      event.preventDefault()
      this.emitExtension(marker, this.maximumExtensionInDaysOf(marker))
    }
  }

  private extensionAt(clientX: number, marker: RetentionMarker): number {
    const { left, width } = this.track.nativeElement.getBoundingClientRect()
    if (width === 0) {
      return this.extensionInDaysOf(marker)
    }
    const fraction = Math.min(1, Math.max(0, (clientX - left) / width))
    const span = this.view.windowEndDate.getTime() - this.view.meetingDate.getTime()
    const date = this.view.meetingDate.getTime() + fraction * span
    const from = marker === 'SOFT' ? this.view.currentSoftDeletionDate : this.view.currentDeletionDate
    return Math.round((date - from.getTime()) / MILLISECONDS_IN_DAY)
  }

  private emitExtension(marker: RetentionMarker, days: number) {
    const clamped = Math.min(this.maximumExtensionInDaysOf(marker), Math.max(this.minimumExtensionInDaysOf(marker), days))
    if (clamped !== this.extensionInDaysOf(marker)) {
      const output = marker === 'SOFT' ? this.softExtensionInDaysChange : this.deletionExtensionInDaysChange
      output.emit(clamped)
    }
  }
}
